import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Typography,
  Checkbox,
  FormControlLabel,
  TextField,
  InputAdornment,
  Divider,
  Radio,
  RadioGroup,
  FormControl,
  FormLabel,
  Button,
  Chip,
  IconButton,
} from '@mui/material';
import {
  Search as SearchIcon,
  AccountBalance as AccountsIcon,
  CreditCard as CardBrandIcon,
  Public as CountryIcon,
  AttachMoney as CurrencyIcon,
  CalendarToday as DateRangeIcon,
  Payment as PaymentMethodsIcon,
  AccountTree as ProvidersIcon,
  CheckCircle as StatusIcon,
  Close as CloseIcon,
  ArrowForward as ArrowForwardIcon,
} from '@mui/icons-material';
import {
  SUPERADMIN_STATUS_GROUPS,
  SUPERADMIN_INVOICE_STATUSES,
} from '@/superadmin/pages/invoice/constants/invoice_status';
import type { SuperadminInvoiceFilters as FilterState } from '@/superadmin/pages/invoice/types/superadminInvoice';

export type FilterCategory =
  | 'accounts'
  | 'cardBrand'
  | 'country'
  | 'currency'
  | 'dateRange'
  | 'installments'
  | 'paymentMethods'
  | 'providers'
  | 'merchants'
  | 'status';

interface FilterPanelProps {
  open: boolean;
  anchorEl: HTMLElement | null;
  onClose: () => void;
  filters: FilterState;
  onFiltersChange: (filters: FilterState) => void;
  onApplyFilters: () => void;
  onClearFilters: () => void;
  merchants: any[];
}

interface FilterCategoryConfig {
  id: FilterCategory;
  label: string;
  icon: React.ReactNode;
}

const filterCategories: FilterCategoryConfig[] = [
  { id: 'dateRange', label: 'Date range', icon: <DateRangeIcon /> },
  { id: 'status', label: 'Status', icon: <StatusIcon /> },
  { id: 'merchants', label: 'Merchants', icon: <AccountsIcon /> },
];

export const FilterPanel: React.FC<FilterPanelProps> = ({
  open,
  anchorEl,
  onClose,
  filters,
  merchants,
  onFiltersChange,
  onApplyFilters,
  onClearFilters,
}) => {
  const [selectedCategory, setSelectedCategory] = useState<FilterCategory>('status');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedDateRangeType, setSelectedDateRangeType] = useState<string>('last3days');
  const [customStartDate, setCustomStartDate] = useState<string>('');
  const [customEndDate, setCustomEndDate] = useState<string>('');
  const [customStartTime, setCustomStartTime] = useState<string>('00:00:00');
  const [customEndTime, setCustomEndTime] = useState<string>('23:59:59');

  const handleCategorySelect = (category: FilterCategory) => {
    setSelectedCategory(category);
    setSearchTerm('');
  };

  const handleStatusChange = (status: string, checked: boolean) => {
    const currentStatuses = filters.statusFilter ? filters.statusFilter.split(',') : [];
    let newStatuses: string[];

    if (checked) {
      newStatuses = [...currentStatuses, status].filter(Boolean);
    } else {
      newStatuses = currentStatuses.filter(s => s !== status);
    }

    onFiltersChange({
      ...filters,
      statusFilter: newStatuses.length > 0 ? newStatuses.join(',') : '',
    });
  };

  const handleMerchantChange = (merchant: any) => {
    onFiltersChange({
      ...filters,
      merchants: [merchant], // ✅ always single
    });
  };

  const isMerchantSelected = (merchant: any) => filters.merchants?.[0]?.uuid === merchant.uuid;

  const getDateRangeForType = (rangeType: string): { startDate: string; endDate: string } => {
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    let startDate: Date;
    let endDate: Date = new Date(today);
    endDate.setHours(23, 59, 59, 999);

    switch (rangeType) {
      case 'today':
        startDate = new Date(today);
        startDate.setHours(0, 0, 0, 0);
        break;
      case 'last3days':
        startDate = new Date(today);
        startDate.setDate(startDate.getDate() - 2); // 3 days including today
        startDate.setHours(0, 0, 0, 0);
        break;
      case 'last7days':
        startDate = new Date(today);
        startDate.setDate(startDate.getDate() - 6); // 7 days including today
        startDate.setHours(0, 0, 0, 0);
        break;
      case 'last30days':
        startDate = new Date(today);
        startDate.setDate(startDate.getDate() - 29); // 30 days including today
        startDate.setHours(0, 0, 0, 0);
        break;
      case 'thisMonth':
        startDate = new Date(now.getFullYear(), now.getMonth(), 1);
        startDate.setHours(0, 0, 0, 0);
        endDate = new Date(now.getFullYear(), now.getMonth() + 1, 0);
        endDate.setHours(23, 59, 59, 999);
        break;
      case 'custom':
        // Custom dates will be handled separately
        return { startDate: '', endDate: '' };
      default:
        return { startDate: '', endDate: '' };
    }

    return {
      startDate: startDate.toISOString(),
      endDate: endDate.toISOString(),
    };
  };

  const handleDateRangeTypeChange = (rangeType: string) => {
    setSelectedDateRangeType(rangeType);

    if (rangeType === 'custom') {
      // For custom, use the current custom date values or set defaults
      const today = new Date();
      const todayStr = today.toISOString().split('T')[0];
      setCustomStartDate(customStartDate || todayStr);
      setCustomEndDate(customEndDate || todayStr);
    } else {
      const dateRange = getDateRangeForType(rangeType);
      onFiltersChange({
        ...filters,
        dateRange: dateRange.startDate
          ? {
              startDate: dateRange.startDate,
              endDate: dateRange.endDate,
            }
          : undefined,
      });
    }
  };

  const handleCustomDateChange = () => {
    if (customStartDate && customEndDate) {
      // Combine date and time
      const startDateTime = new Date(`${customStartDate}T${customStartTime}`);
      const endDateTime = new Date(`${customEndDate}T${customEndTime}`);

      console.log('FilterPanel - Setting date range:', {
        startDate: startDateTime.toISOString(),
        endDate: endDateTime.toISOString(),
      });

      onFiltersChange({
        ...filters,
        dateRange: {
          startDate: startDateTime.toISOString(),
          endDate: endDateTime.toISOString(),
        },
      });
    }
  };

  const formatDateForDisplay = (dateString: string): string => {
    if (!dateString) return '';
    const date = new Date(dateString);
    const months = [
      'Jan',
      'Feb',
      'Mar',
      'Apr',
      'May',
      'Jun',
      'Jul',
      'Aug',
      'Sep',
      'Oct',
      'Nov',
      'Dec',
    ];
    const days = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
    return `${days[date.getDay()]}, ${months[date.getMonth()]} ${date.getDate()}`;
  };

  const getDateRangeLabel = (rangeType: string): string => {
    const dateRange = getDateRangeForType(rangeType);
    if (!dateRange.startDate) return '';

    const start = new Date(dateRange.startDate);
    const end = new Date(dateRange.endDate);
    const formatDate = (d: Date) => {
      const months = [
        'Jan',
        'Feb',
        'Mar',
        'Apr',
        'May',
        'Jun',
        'Jul',
        'Aug',
        'Sep',
        'Oct',
        'Nov',
        'Dec',
      ];
      return `${months[d.getMonth()]} ${d.getDate()}`;
    };

    if (start.getTime() === end.getTime()) {
      return formatDate(start);
    }
    return `${formatDate(start)} - ${formatDate(end)}`;
  };

  const isStatusSelected = (status: string) => {
    const currentStatuses = filters.statusFilter ? filters.statusFilter.split(',') : [];
    return currentStatuses.includes(status);
  };

  const renderFilterContent = () => {
    switch (selectedCategory) {
      case 'status':
        return (
          <Box sx={{ p: 2 }}>
            <TextField
              fullWidth
              size="small"
              placeholder="Search"
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                ),
              }}
              sx={{ mb: 2 }}
            />

            {/* ================= TRANSACTION STATUS ================= */}
            <Box
              sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}
            >
              <Typography variant="subtitle2" fontWeight="bold">
                Invoice Status
              </Typography>
              <Button
                size="small"
                onClick={() =>
                  onFiltersChange({
                    ...filters,
                    statusFilter: Object.keys(SUPERADMIN_INVOICE_STATUSES).join(','),
                  })
                }
                sx={{ textTransform: 'none', fontSize: '0.75rem' }}
              >
                Select all
              </Button>
            </Box>

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mb: 3 }}>
              {Object.entries(SUPERADMIN_INVOICE_STATUSES).map(([key, label]) => (
                <FormControlLabel
                  key={key}
                  control={
                    <Radio
                      checked={filters.statusFilter === key}
                      onChange={() =>
                        onFiltersChange({
                          ...filters,
                          statusFilter: key,
                        })
                      }
                    />
                  }
                  label={label}
                />
              ))}
            </Box>
          </Box>
        );

      case 'merchants':
        return (
          <Box sx={{ p: 2 }}>
            <TextField
              fullWidth
              size="small"
              placeholder="Search merchants"
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                ),
              }}
              sx={{ mb: 2 }}
            />

            <Box
              sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}
            >
              <Typography variant="subtitle2" fontWeight="bold">
                Merchants
              </Typography>

              <Button
                size="small"
                onClick={() =>
                  onFiltersChange({
                    ...filters,
                    merchants: merchants, // ✅ select all
                  })
                }
                sx={{ textTransform: 'none', fontSize: '0.75rem' }}
              >
                Select all
              </Button>
            </Box>

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              {merchants
                .filter(
                  m => !searchTerm || m.name?.toLowerCase().includes(searchTerm.toLowerCase())
                )
                .map(merchant => (
                  <FormControlLabel
                    key={merchant.uuid}
                    control={
                      <Radio
                        checked={filters.merchants?.[0]?.uuid === merchant.uuid}
                        onChange={() =>
                          onFiltersChange({
                            ...filters,
                            merchants: [merchant],
                          })
                        }
                      />
                    }
                    label={merchant.name}
                  />
                ))}
            </Box>
          </Box>
        );

      case 'dateRange':
        return (
          <Box sx={{ p: 2 }}>
            <FormControl component="fieldset" fullWidth>
              <RadioGroup
                value={selectedDateRangeType}
                onChange={e => handleDateRangeTypeChange(e.target.value)}
              >
                <Box sx={{ mb: 2 }}>
                  <FormControlLabel
                    value="custom"
                    control={<Radio />}
                    label={
                      <Box>
                        <Typography variant="body2">Custom</Typography>
                      </Box>
                    }
                  />
                  {selectedDateRangeType === 'custom' && (
                    <Box sx={{ ml: 4, mt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
                      <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                        <TextField
                          label="Start date"
                          type="date"
                          size="small"
                          value={customStartDate}
                          onChange={e => {
                            setCustomStartDate(e.target.value);
                            setTimeout(handleCustomDateChange, 0);
                          }}
                          InputLabelProps={{ shrink: true }}
                          sx={{ flex: 1, minWidth: 150 }}
                        />
                        <TextField
                          label="End date"
                          type="date"
                          size="small"
                          value={customEndDate}
                          onChange={e => {
                            setCustomEndDate(e.target.value);
                            setTimeout(handleCustomDateChange, 0);
                          }}
                          InputLabelProps={{ shrink: true }}
                          sx={{ flex: 1, minWidth: 150 }}
                        />
                      </Box>
                      {/* <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                        <TextField
                          label="Start hour"
                          type="time"
                          size="small"
                          value={customStartTime}
                          onChange={(e) => {
                            setCustomStartTime(e.target.value);
                            setTimeout(handleCustomDateChange, 0);
                          }}
                          InputLabelProps={{ shrink: true }}
                          inputProps={{ step: 1 }}
                          sx={{ flex: 1, minWidth: 150 }}
                        />
                        <TextField
                          label="End hour"
                          type="time"
                          size="small"
                          value={customEndTime}
                          onChange={(e) => {
                            setCustomEndTime(e.target.value);
                            setTimeout(handleCustomDateChange, 0);
                          }}
                          InputLabelProps={{ shrink: true }}
                          inputProps={{ step: 1 }}
                          sx={{ flex: 1, minWidth: 150 }}
                        />
                      </Box> */}
                    </Box>
                  )}
                </Box>

                <FormControlLabel
                  value="today"
                  control={<Radio />}
                  label={
                    <Box>
                      <Typography variant="body2">Today</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {formatDateForDisplay(new Date().toISOString())}
                      </Typography>
                    </Box>
                  }
                />

                <FormControlLabel
                  value="last3days"
                  control={<Radio />}
                  label={
                    <Box>
                      <Typography variant="body2">Last 3 days</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {getDateRangeLabel('last3days')}
                      </Typography>
                    </Box>
                  }
                />

                <FormControlLabel
                  value="last7days"
                  control={<Radio />}
                  label={
                    <Box>
                      <Typography variant="body2">Last 7 days</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {getDateRangeLabel('last7days')}
                      </Typography>
                    </Box>
                  }
                />

                <FormControlLabel
                  value="last30days"
                  control={<Radio />}
                  label={
                    <Box>
                      <Typography variant="body2">Last 30 days</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {getDateRangeLabel('last30days')}
                      </Typography>
                    </Box>
                  }
                />

                <FormControlLabel
                  value="thisMonth"
                  control={<Radio />}
                  label={
                    <Box>
                      <Typography variant="body2">This month</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {new Date().toLocaleDateString('en-US', {
                          month: 'short',
                          year: 'numeric',
                        })}
                      </Typography>
                    </Box>
                  }
                />
              </RadioGroup>
            </FormControl>
          </Box>
        );

      default:
        return (
          <Box sx={{ p: 2 }}>
            <Typography variant="body2" color="text.secondary">
              Filter options for {filterCategories.find(c => c.id === selectedCategory)?.label}{' '}
              coming soon.
            </Typography>
          </Box>
        );
    }
  };

  const getActiveFiltersCount = () => {
  let count = 0;

  if (filters.statusFilter) count += 1;
  if (filters.merchants?.length) count += 1;
  if (filters.dateRange?.startDate && filters.dateRange?.endDate) count += 1;

  return count;
};


  // Initialize date range on mount if filters already have a date range
  useEffect(() => {
    if (filters.dateRange?.startDate && filters.dateRange?.endDate) {
      // Try to determine which predefined range matches, or set to custom
      const startDate = new Date(filters.dateRange.startDate);
      const endDate = new Date(filters.dateRange.endDate);

      // Check if it matches any predefined range
      const today = new Date();
      today.setHours(0, 0, 0, 0);

      const isToday =
        startDate.toDateString() === today.toDateString() &&
        endDate.toDateString() === today.toDateString();
      if (isToday) {
        setSelectedDateRangeType('today');
        return;
      }

      // Check for last 3, 7, 30 days
      const daysDiff = Math.floor(
        (endDate.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24)
      );
      if (daysDiff === 2) setSelectedDateRangeType('last3days');
      else if (daysDiff === 6) setSelectedDateRangeType('last7days');
      else if (daysDiff === 29) setSelectedDateRangeType('last30days');
      else {
        setSelectedDateRangeType('custom');
        setCustomStartDate(startDate.toISOString().split('T')[0]);
        setCustomEndDate(endDate.toISOString().split('T')[0]);
        // Extract time from ISO string
        const startTime = startDate.toTimeString().split(' ')[0].substring(0, 8);
        const endTime = endDate.toTimeString().split(' ')[0].substring(0, 8);
        setCustomStartTime(startTime);
        setCustomEndTime(endTime);
      }
    }
  }, [filters.dateRange]);

  const [position, setPosition] = useState({ top: 0, left: 0 });

  // Update position on mount, scroll, and resize
  useEffect(() => {
    if (!open || !anchorEl) {
      setPosition({ top: 0, left: 0 });
      return;
    }

    const updatePosition = () => {
      if (!anchorEl) {
        setPosition({ top: 0, left: 0 });
        return;
      }
      const rect = anchorEl.getBoundingClientRect();
      setPosition({
        top: rect.bottom + 8,
        left: rect.left,
      });
    };

    // Initial position
    updatePosition();

    // Update on scroll
    const handleScroll = () => {
      updatePosition();
    };

    // Update on resize
    const handleResize = () => {
      updatePosition();
    };

    // Add event listeners
    window.addEventListener('scroll', handleScroll, true);
    window.addEventListener('resize', handleResize);

    // Cleanup
    return () => {
      window.removeEventListener('scroll', handleScroll, true);
      window.removeEventListener('resize', handleResize);
    };
  }, [open, anchorEl]);

  if (!open) return null;

  return (
    <Paper
      elevation={8}
      sx={{
        position: 'fixed',
        top: `${position.top}px`,
        left: `${position.left}px`,
        width: { xs: '70vw', sm: 800 },
        maxHeight: '80vh',
        display: 'flex',
        flexDirection: 'column',
        zIndex: 1300,
        borderRadius: 2,
        overflow: 'hidden',
      }}
    >
      {/* CONTENT WRAPPER — FIXED */}
      <Box
        sx={{
          display: 'flex', // <<< horizontal layout
          flex: 1,
          overflow: 'hidden',
        }}
      >
        {/* Left Sidebar */}
        <Box
          sx={{
            width: 240,
            borderRight: '1px solid',
            borderColor: 'divider',
            bgcolor: 'background.paper',
            overflowY: 'auto',
          }}
        >
          <Box
            sx={{ p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
          >
            <Typography variant="h6" fontWeight="bold">
              Filters
            </Typography>
            <IconButton size="small" onClick={onClose}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>

          <Divider />

          <List sx={{ p: 0 }}>
            {filterCategories.map(category => (
              <ListItem key={category.id} disablePadding>
                <ListItemButton
                  selected={selectedCategory === category.id}
                  onClick={() => handleCategorySelect(category.id)}
                  sx={{
                    borderLeft:
                      selectedCategory === category.id ? '3px solid' : '3px solid transparent',
                    borderColor: selectedCategory === category.id ? 'primary.main' : 'transparent',
                    bgcolor: selectedCategory === category.id ? 'action.selected' : 'transparent',
                    '&:hover': { bgcolor: 'action.hover' },
                    py: 1.5,
                    px: 2,
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, width: '100%' }}>
                    <Box
                      sx={{
                        color: selectedCategory === category.id ? 'primary.main' : 'text.secondary',
                      }}
                    >
                      {category.icon}
                    </Box>

                    <ListItemText
                      primary={category.label}
                      primaryTypographyProps={{
                        fontSize: '0.875rem',
                        fontWeight: selectedCategory === category.id ? 600 : 400,
                      }}
                    />

                    {selectedCategory === category.id && (
                      <ArrowForwardIcon fontSize="small" sx={{ color: 'primary.main' }} />
                    )}
                  </Box>
                </ListItemButton>
              </ListItem>
            ))}
          </List>
        </Box>

        {/* Right Panel */}
        <Box
          sx={{
            flex: 1,
            overflowY: 'auto',
            bgcolor: 'background.default',
            minHeight: 400,
            p: 2,
            pb: 8,
          }}
        >
          {renderFilterContent()}
        </Box>
      </Box>

      {/* Footer */}
      <Box
        sx={{
          p: 2,
          bgcolor: 'background.paper',
          borderTop: '1px solid',
          borderColor: 'divider',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexShrink: 0,
        }}
      >
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          {getActiveFiltersCount() > 0 && (
            <Chip
              label={`${getActiveFiltersCount()} filter${getActiveFiltersCount() > 1 ? 's' : ''} applied`}
              size="small"
              onDelete={onClearFilters}
              color="primary"
              variant="outlined"
            />
          )}
        </Box>

        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button size="small" onClick={onClearFilters} variant="outlined">
            Clear
          </Button>
          <Button size="small" onClick={onApplyFilters} variant="contained" color="primary">
            Apply Filters
          </Button>
        </Box>
      </Box>
    </Paper>
  );
};
